'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';

const CHASHI_SYSTEM_PROMPT = `তুমি "চাষী" — বাংলাদেশের কৃষকদের জন্য একজন বিশেষজ্ঞ কৃষি উপদেষ্টা। তুমি শুধুমাত্র বাংলা ভাষায় উত্তর দেবে।

তোমার বিশেষজ্ঞতার ক্ষেত্রসমূহ:

## ফসল বিশ্লেষণ ও রোগ নির্ণয়:
- ধান: ব্লাস্ট, বাদামি দাগ, শীষ পোড়া, টুংরো ভাইরাস, পাতা পোড়া রোগ
- গম: মরিচা রোগ, পাউডারি মিলডিউ, ব্লাইট
- পাট: কান্ড পচা, অ্যানথ্রাকনোজ
- সবজি: টমেটো, বেগুন, মরিচ, আলু, পেঁয়াজ, রসুনের রোগ ও পোকামাকড়
- ফল: আম, কলা, পেঁপে, লিচু, কাঁঠালের রোগ ও পরিচর্যা

## বাংলাদেশের কৃষি মৌসুম:
- খরিফ-১ (মার্চ-জুন): আউশ ধান, পাট, তিল
- খরিফ-২ (জুলাই-নভেম্বর): আমন ধান, সবজি
- রবি (নভেম্বর-মার্চ): বোরো ধান, গম, সরিষা, আলু, শীতকালীন সবজি

## সার ব্যবস্থাপনা (বাংলাদেশ কৃষি গবেষণা ইনস্টিটিউট অনুযায়ী):
- ধান: ইউরিয়া ১৬০-২০০ কেজি/হেক্টর, টিএসপি ৮০-১০০ কেজি/হেক্টর, এমওপি ৬০-৮০ কেজি/হেক্টর
- গম: ইউরিয়া ১৫০-১৮০ কেজি/হেক্টর, টিএসপি ৭০-৯০ কেজি/হেক্টর
- সবজি: জৈব সার ১০-১৫ টন/হেক্টর + রাসায়নিক সার পরিমাণমতো
- সরিষা: ইউরিয়া ১০০-১২০ কেজি/হেক্টর, টিএসপি ৬০-৮০ কেজি/হেক্টর

## কীটনাশক ও বালাইনাশক:
- ধানের মাজরা পোকা: কার্বোফুরান ৩জি, ক্লোরপাইরিফস
- ধানের বাদামি গাছফড়িং: ইমিডাক্লোপ্রিড, বুপ্রোফেজিন
- সবজির সাদামাছি: ইমিডাক্লোপ্রিড ৭০ ডব্লিউজি
- ছত্রাকনাশক: ট্রাইসাইক্লাজোল (ধান ব্লাস্ট), ম্যানকোজেব (আলু ব্লাইট)
- জৈব বালাইনাশক: নিম তেল, বাসুডিন

## সেচ ব্যবস্থাপনা:
- বোরো ধান: ৩-৫ সেমি দাঁড়ানো পানি বজায় রাখা
- আমন ধান: বৃষ্টিনির্ভর, প্রয়োজনে সম্পূরক সেচ
- সবজি: ড্রিপ সেচ বা ফুরো সেচ পদ্ধতি

## মাটি পরীক্ষা ও উর্বরতা:
- বাংলাদেশের মাটির pH: ৫.৫-৭.০ আদর্শ
- জৈব পদার্থ বৃদ্ধি: সবুজ সার, কম্পোস্ট, ভার্মিকম্পোস্ট
- মাটি পরীক্ষা: উপজেলা কৃষি অফিস বা BADC থেকে

## আবহাওয়া ও জলবায়ু অভিযোজন:
- বন্যা সহনশীল ধান: BRRI dhan51, BRRI dhan52, BINA dhan11
- খরা সহনশীল: BRRI dhan56, BRRI dhan66
- লবণ সহনশীল: BRRI dhan47, BRRI dhan61, BINA dhan8

## সরকারি সহায়তা ও যোগাযোগ:
- কৃষি সম্প্রসারণ অধিদপ্তর (DAE): ১৬১২৩
- বাংলাদেশ কৃষি গবেষণা ইনস্টিটিউট (BARI)
- বাংলাদেশ ধান গবেষণা ইনস্টিটিউট (BRRI)
- কৃষি তথ্য সার্ভিস (AIS): www.ais.gov.bd

সবসময় বাংলায় উত্তর দাও। সহজ ও বোধগম্য ভাষায় কৃষকদের সাহায্য করো। প্রয়োজনে ধাপে ধাপে পরামর্শ দাও।`;

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChashiChatbotProps {
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}

// Extend Window for SpeechRecognition
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

export default function ChashiChatbot({ externalOpen, onExternalOpenChange }: ChashiChatbotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'আসসালামু আলাইকুম! আমি চাষী — আপনার কৃষি বিশেষজ্ঞ সহায়ক। ফসলের রোগ, সার, কীটনাশক, বা যেকোনো কৃষি বিষয়ে আমাকে জিজ্ঞেস করুন। আমি সবসময় আপনার পাশে আছি! 🌾',
    },
  ]);
  const [streamingText, setStreamingText] = useState('');

  // Voice input state
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  // TTS state
  const [speakingMsgIndex, setSpeakingMsgIndex] = useState<number | null>(null);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(true);
  const lastSpokenIndexRef = useRef<number>(-1);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { response, isLoading, error, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    true
  );

  // ── Speech Recognition setup ──────────────────────────────────────────────
  const initRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;
    const rec = new SpeechRecognition();
    rec.lang = 'bn-BD';
    rec.continuous = false;
    rec.interimResults = true;
    rec.onresult = (event: any) => {
      const transcript = Array.from(event.results as SpeechRecognitionResultList)
        .map((r: SpeechRecognitionResult) => r[0].transcript)
        .join('');
      setInput(transcript);
    };
    rec.onerror = () => {
      setIsListening(false);
      toast.error('মাইক্রোফোন ব্যবহারে সমস্যা হয়েছে।');
    };
    rec.onend = () => setIsListening(false);
    return rec;
  }, []);

  const toggleListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('আপনার ব্রাউজার ভয়েস ইনপুট সমর্থন করে না।');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      const rec = initRecognition();
      if (!rec) return;
      recognitionRef.current = rec;
      rec.start();
      setIsListening(true);
    }
  };

  // ── Text-to-Speech ────────────────────────────────────────────────────────
  const speakText = useCallback((text: string, msgIndex: number) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    // Prefer Bengali voice; fall back to any available
    const voices = window.speechSynthesis.getVoices();
    const banglaVoice = voices.find(
      (v) => v.lang === 'bn-BD' || v.lang === 'bn-IN' || v.lang.startsWith('bn')
    );
    if (banglaVoice) utter.voice = banglaVoice;
    utter.lang = 'bn-BD';
    utter.rate = 0.9;
    utter.pitch = 1;
    setSpeakingMsgIndex(msgIndex);
    utter.onend = () => setSpeakingMsgIndex(null);
    utter.onerror = () => setSpeakingMsgIndex(null);
    window.speechSynthesis.speak(utter);
  }, []);

  const stopSpeaking = () => {
    window.speechSynthesis?.cancel();
    setSpeakingMsgIndex(null);
  };

  const toggleSpeak = (text: string, msgIndex: number) => {
    if (speakingMsgIndex === msgIndex) {
      stopSpeaking();
    } else {
      speakText(text, msgIndex);
    }
  };

  // ── Auto-play latest AI response ─────────────────────────────────────────
  useEffect(() => {
    if (!autoPlayEnabled || isLoading) return;
    const lastIdx = messages.length - 1;
    const lastMsg = messages[lastIdx];
    if (
      lastMsg?.role === 'assistant' &&
      lastIdx !== lastSpokenIndexRef.current &&
      lastIdx > 0 // skip the initial greeting auto-play
    ) {
      lastSpokenIndexRef.current = lastIdx;
      // Small delay to let browser voices load
      setTimeout(() => speakText(lastMsg.content, lastIdx), 400);
    }
  }, [messages, isLoading, autoPlayEnabled, speakText]);

  // ── Existing effects ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleGlobalOpen = () => setIsOpen(true);
    window.addEventListener('chashi-open', handleGlobalOpen);
    return () => window.removeEventListener('chashi-open', handleGlobalOpen);
  }, []);

  useEffect(() => {
    if (externalOpen !== undefined) setIsOpen(externalOpen);
  }, [externalOpen]);

  const handleToggle = (val: boolean) => {
    setIsOpen(val);
    onExternalOpenChange?.(val);
    if (!val) stopSpeaking();
  };

  useEffect(() => {
    if (error) toast.error('চাষী সংযোগে সমস্যা হয়েছে। আবার চেষ্টা করুন।');
  }, [error]);

  useEffect(() => {
    if (isLoading) {
      setStreamingText(response || '');
    } else if (response && !isLoading) {
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === response) return prev;
        const filtered = prev.filter(
          (m, i) =>
            !(
              i === prev.length - 1 &&
              m.role === 'assistant' &&
              m.content === streamingText &&
              streamingText !== ''
            )
        );
        return [...filtered, { role: 'assistant', content: response }];
      });
      setStreamingText('');
    }
  }, [response, isLoading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  // ── Send message ──────────────────────────────────────────────────────────
  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    // Stop any ongoing speech before sending
    stopSpeaking();
    // Stop listening if active
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    }

    const userMessage: Message = { role: 'user', content: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');

    const apiMessages = [
      { role: 'system', content: CHASHI_SYSTEM_PROMPT },
      ...updatedMessages.map((m) => ({ role: m.role, content: m.content })),
    ];
    sendMessage(apiMessages, { temperature: 0.7, max_tokens: 1024 });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickQuestions = [
    'ধানের ব্লাস্ট রোগের চিকিৎসা কী?',
    'বোরো ধানে কতটুকু সার দিতে হবে?',
    'টমেটোর পাতা হলুদ হয়ে যাচ্ছে কেন?',
  ];

  return (
    <>
      {/* Floating Avatar Button */}
      <button
        onClick={() => handleToggle(!isOpen)}
        className="fixed bottom-6 right-6 z-50 group"
        aria-label="চাষী কৃষি সহায়তা"
      >
        <div className="relative">
          {!isOpen && (
            <span className="absolute inset-0 rounded-full bg-green-400 opacity-30 animate-ping" />
          )}
          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-700 shadow-2xl flex items-center justify-center border-4 border-white transition-transform duration-200 group-hover:scale-110">
            {isOpen ? (
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 0 0 8 20C19 20 22 3 22 3c-1 2-8 2-8 2C14 3 9 3 6 7c-1.5 2-2 4-2 4s2-1 4-1c2 0 3 1 3 1s-2 0-3 2c-1 2-1 4-1 4s1-1 3-1c1.5 0 2.5.5 3 1 .5.5.5 1.5.5 1.5S17 16 17 14c0-1-.5-2-1-3 1 0 3 1 3 3 0 3-2 6-2 6s4-3 4-8c0-3-2-6-4-4z"/>
              </svg>
            )}
          </div>
          {!isOpen && (
            <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-green-700 text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              চাষী AI
            </div>
          )}
          {!isOpen && (
            <span className="absolute top-0 right-0 w-4 h-4 bg-yellow-400 rounded-full border-2 border-white flex items-center justify-center">
              <span className="text-[8px] font-bold text-green-900">AI</span>
            </span>
          )}
        </div>
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-28 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 max-h-[80vh] flex flex-col bg-white rounded-2xl shadow-2xl border border-green-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/40 flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 0 0 8 20C19 20 22 3 22 3c-1 2-8 2-8 2C14 3 9 3 6 7c-1.5 2-2 4-2 4s2-1 4-1c2 0 3 1 3 1s-2 0-3 2c-1 2-1 4-1 4s1-1 3-1c1.5 0 2.5.5 3 1 .5.5.5 1.5.5 1.5S17 16 17 14c0-1-.5-2-1-3 1 0 3 1 3 3 0 3-2 6-2 6s4-3 4-8c0-3-2-6-4-4z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-base leading-tight">চাষী</h3>
              <p className="text-green-100 text-xs truncate">কৃষি বিশেষজ্ঞ সহায়তা</p>
            </div>
            {/* Auto-play toggle */}
            <button
              onClick={() => {
                setAutoPlayEnabled((v) => !v);
                if (autoPlayEnabled) stopSpeaking();
              }}
              title={autoPlayEnabled ? 'অটো-প্লে বন্ধ করুন' : 'অটো-প্লে চালু করুন'}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                autoPlayEnabled ? 'bg-white/20 text-white' : 'bg-white/10 text-white/40'
              }`}
            >
              {/* Speaker icon */}
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
              </svg>
            </button>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
              <span className="text-green-100 text-xs">সক্রিয়</span>
            </div>
            <button
              onClick={() => handleToggle(false)}
              className="ml-1 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="বন্ধ করুন"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div
            className="flex-1 overflow-y-auto p-3 space-y-3 bg-gradient-to-b from-green-50/30 to-white min-h-0"
            style={{ maxHeight: '380px' }}
          >
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-sm flex-shrink-0 mt-0.5 overflow-hidden">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                    </svg>
                  </div>
                )}
                <div className="flex flex-col gap-1 max-w-[80%]">
                  <div
                    className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user' ?'bg-green-600 text-white rounded-tr-sm' :'bg-white text-gray-800 rounded-tl-sm shadow-sm border border-green-100'
                    }`}
                  >
                    {msg.content}
                  </div>
                  {/* Speaker button for assistant messages */}
                  {msg.role === 'assistant' && (
                    <button
                      onClick={() => toggleSpeak(msg.content, i)}
                      title={speakingMsgIndex === i ? 'থামান' : 'শুনুন'}
                      className={`self-start flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                        speakingMsgIndex === i
                          ? 'bg-green-100 border-green-400 text-green-700' :'bg-white border-green-200 text-green-600 hover:bg-green-50'
                      }`}
                    >
                      {speakingMsgIndex === i ? (
                        <>
                          {/* Stop / speaker-wave icon */}
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                          </svg>
                          থামান
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                          </svg>
                          শুনুন
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Streaming indicator */}
            {isLoading && (
              <div className="flex gap-2 flex-row">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-sm flex-shrink-0 mt-0.5 overflow-hidden">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                </div>
                <div className="max-w-[80%] px-3 py-2 rounded-2xl rounded-tl-sm bg-white text-gray-800 text-sm shadow-sm border border-green-100">
                  {streamingText || (
                    <span className="flex gap-1 items-center py-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length === 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {quickQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setInput(q);
                    inputRef.current?.focus();
                  }}
                  className="text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-1 hover:bg-green-100 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input bar with mic + send */}
          <div className="p-3 border-t border-green-100 bg-white">
            <div className="flex gap-2 items-center">
              {/* Mic button */}
              <button
                onClick={toggleListening}
                disabled={isLoading}
                title={isListening ? 'রেকর্ডিং বন্ধ করুন' : 'ভয়েস বার্তা পাঠান'}
                className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isListening
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse' :'bg-green-100 hover:bg-green-200 text-green-700'
                }`}
              >
                {isListening ? (
                  /* Mic-off / stop icon */
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
                  </svg>
                ) : (
                  /* Mic icon */
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                )}
              </button>

              {/* Text input */}
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isListening ? 'শুনছি...' : 'আপনার প্রশ্ন লিখুন বা বলুন...'}
                disabled={isLoading}
                className="flex-1 text-sm border border-green-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:opacity-60 bg-green-50/50 placeholder-gray-400"
              />

              {/* Send button */}
              <button
                onClick={handleSend}
                disabled={isLoading || !input.trim()}
                className="w-9 h-9 rounded-xl bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p className="text-center text-[10px] text-gray-400 mt-1.5">চাষী — বাংলাদেশ কৃষি বিশেষজ্ঞ AI</p>
          </div>
        </div>
      )}
    </>
  );
}
